import {
  CommonModule,
} from "@angular/common";
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { NgxMaskDirective } from "ngx-mask";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ErrorMessage } from "src/utils/error-message";
import { LabelMode } from "src/utils/label-mode.enum";
import { GenerateRandom } from "src/utils/random";
import { KeeniconComponent } from "../keenicon/keenicon.component";

@Component({
  selector: "app-input",
  templateUrl: "./input.component.html",
  styleUrls: ["./input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, NgxMaskDirective, KeeniconComponent],
})
export class InputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() name = GenerateRandom(6);
  @Input() type = "text";
  @Input() autocomplete = "off";
  @Input() label: string;
  @Input() addonLabel = "";
  @Input() labelValue = "";
  @Input() placeholder = "";
  @Input() mask: string;
  @Input() dropSpecialCharacters = false;
  @Input() allowNegativeNumbers = false;
  @Input() thousandSeparator: string;
  @Input() specialCharacters: string[] = [];
  @Input() minlength = 0;
  @Input() maxlength = 1000;
  @Input() tabindex = 0;
  @Input() maskPrefix = "";
  @Input() maskSuffix = "";
  @Input() suffix: string;
  @Input() inputClass: string;
  @Input() classes: string;
  @Input() errorMessage = "VALIDATOR.REQUIRED";
  @Input() showErrorMessage = true;
  @Input() minVal = null;
  @Input() maxVal = null;
  @Input() required = false;

  @Input() debouceTime = 0;

  @Output() valueChange = new EventEmitter();
  @Output() inputChange = new EventEmitter();

  subscription: Subscription;

  LabelMode = LabelMode;
  mode: LabelMode = LabelMode.DEFAULT;

  form = new FormControl();

  isError = false;
  isValid = false;

  showPwd = false;
  readonly = false;

  isFormatCurrency = false;

  constructor() {}

  @Input()
  set isDisabled(value: boolean) {
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  @Input()
  set isReadyOnly(value: boolean) {
    this.readonly = value;
  }

  @Input()
  set error(value: any) {
    this.isError = false;
    this.errorMessage = "";

    if (value !== null && value !== undefined && value !== false) {
      this.isError = true;
      this.errorMessage = new ErrorMessage().getErrorMessage(value);
    }
  }

  @Input()
  set valid(value: any) {
    this.isValid = false;
    if (value !== null && value !== undefined && value !== "") {
      this.isValid = true;
    }
  }

  @Input()
  set value(value: any) {
    if (value !== undefined) {
      this.writeValue(value);
    }
  }

  @Input() set isCurrency(value: boolean) {
    if (value) {
      this.mask = "separator.2";
      this.thousandSeparator = ",";
      this.dropSpecialCharacters = true;
      this.isFormatCurrency = true;
    }
  }

  @Input() set labelMode(value: LabelMode) {
    this.mode = value;
  }

  onChange = (value: string) => {};
  onTouched = (value: string) => {};

  ngOnInit(): void {
    this.subscription = this.form.valueChanges
      .pipe(debounceTime(this.debouceTime), distinctUntilChanged())
      .subscribe((val) => {
        let value = this.form.value;
        // if (this.isFormatCurrency) {
        //   value = this.formatCurrency(value);
        // }
        if (typeof this.form.value === "string") {
          value = this.form.value.trim();
        }
        this.valueChange.emit(value);
        this.onChange(value);
      });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
  formatCurrency(value: number | string): string {
    if (value.toString().includes(".")) {
      value = value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    } else {
      value = value.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }
    return String(value);
  }

  writeValue(obj: any): void {
    // Use patchValue fot unmatch key
    this.form.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  onValueChange(event: string) {}

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onInputChange(value?: any) {
    const inputElement = value.target as HTMLInputElement;
    let inputValue: string | number = inputElement.value;
    if (this.isFormatCurrency) {
      inputValue = +inputValue.toString().replace(/,/g, "");
    }
    this.inputChange.emit(inputValue);
  }

  setDisabledState?(isDisabled: boolean): void {}
}
